import React, { useEffect, useState } from "react";
import { Trash2, MessageCircle, Mail, Download, Send, X } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STORE_URL = (typeof window !== "undefined" ? window.location.origin : "");

const fillTemplate = (tpl, lead) => {
    const nome = (lead?.name || "").trim();
    const primeiro = nome.split(" ")[0] || nome;
    return (tpl || "")
        .replace(/\{nome\}/g, nome)
        .replace(/\{primeiro_nome\}/g, primeiro)
        .replace(/\{link\}/g, STORE_URL);
};

const buildWaLink = (whatsapp, msg) => {
    const num = (whatsapp || "").replace(/\D/g, "");
    const full = num.startsWith("55") ? num : `55${num}`;
    const text = encodeURIComponent(msg || "");
    return `https://wa.me/${full}?text=${text}`;
};

const BroadcastModal = ({ items, template, onClose }) => {
    const [idx, setIdx] = useState(0);
    const current = items[idx];

    if (!current) return null;
    const msg = fillTemplate(template, current);
    const url = buildWaLink(current.whatsapp, msg);

    const sendThis = () => {
        window.open(url, "_blank", "noopener,noreferrer");
    };
    const next = () => {
        if (idx + 1 >= items.length) { onClose(); return; }
        setIdx(idx + 1);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg rounded-sm shadow-2xl" data-testid="broadcast-modal">
                <div className="border-b border-brand-border px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-brand-gold">Envio em massa</div>
                        <h3 className="font-heading font-bold text-lg text-brand-ink">Lead {idx + 1} de {items.length}</h3>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 hover:bg-brand-bg rounded-sm flex items-center justify-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="bg-brand-bg rounded-sm p-3 text-sm">
                        <div className="font-semibold text-brand-ink">{current.name}</div>
                        <div className="text-xs text-brand-inkSoft mt-0.5">{current.whatsapp}</div>
                    </div>

                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-brand-inkSoft mb-1">Mensagem</div>
                        <div className="bg-[#e7ffd6] border border-[#c5e8a5] rounded-sm p-3 text-sm whitespace-pre-wrap leading-relaxed">{msg}</div>
                    </div>

                    <div className="text-xs text-brand-inkSoft bg-brand-bg/60 border-l-2 border-brand-gold pl-3 py-2">
                        Ao clicar em <strong>"Abrir conversa"</strong>, o WhatsApp Web/App abrirá em uma nova aba com a mensagem pré-preenchida. <strong>Confirme o envio</strong> dentro do WhatsApp e volte aqui para passar ao próximo.
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 pt-2">
                        <button onClick={sendThis} data-testid="broadcast-open-wa" className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-success text-white hover:bg-brand-success/90 px-4 py-3 text-sm font-bold rounded-sm">
                            <MessageCircle className="w-4 h-4" /> Abrir conversa
                        </button>
                        <button onClick={next} data-testid="broadcast-next" className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-ink text-white hover:bg-brand-dark px-4 py-3 text-sm font-bold rounded-sm">
                            {idx + 1 < items.length ? "Próximo lead →" : "Finalizar"}
                        </button>
                    </div>

                    <button onClick={onClose} className="w-full text-xs text-brand-inkSoft hover:text-brand-ink underline">Cancelar envio em massa</button>
                </div>
            </div>
        </div>
    );
};

const Leads = () => {
    const [items, setItems] = useState([]);
    const [template, setTemplate] = useState("");
    const [broadcasting, setBroadcasting] = useState(false);

    const load = () => api.get("/admin/leads").then(({ data }) => setItems(data));
    const loadTpl = () => api.get("/settings").then(({ data }) => setTemplate(data.whatsapp_message_template || ""));
    useEffect(() => { load(); loadTpl(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Excluir lead?")) return;
        await api.delete(`/admin/leads/${id}`);
        toast.success("Excluído");
        load();
    };

    const exportCSV = () => {
        const header = "Nome,WhatsApp,Email,Data\n";
        const rows = items.map((l) => `"${l.name}","${l.whatsapp}","${l.email || ""}","${new Date(l.created_at).toLocaleString("pt-BR")}"`).join("\n");
        const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const startBroadcast = () => {
        if (!template?.trim()) {
            toast.error("Defina a mensagem padrão em Gestão visual → Mensagem padrão de WhatsApp antes de enviar.");
            return;
        }
        if (items.length === 0) { toast.error("Não há leads."); return; }
        setBroadcasting(true);
    };

    return (
        <div data-testid="admin-leads-page" className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-brand-gold mb-1">Contatos</div>
                    <h1 className="font-heading font-bold text-3xl text-brand-ink">Leads</h1>
                    <p className="text-sm text-brand-inkSoft mt-1">{items.length} contato{items.length !== 1 ? "s" : ""} cadastrado{items.length !== 1 ? "s" : ""} via formulário "Receba novidades".</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {items.length > 0 && (
                        <button onClick={startBroadcast} data-testid="leads-broadcast" className="inline-flex items-center gap-2 bg-brand-success text-white hover:bg-brand-success/90 px-4 py-2.5 text-sm font-bold rounded-sm">
                            <Send className="w-4 h-4" /> Enviar novidade p/ todos
                        </button>
                    )}
                    {items.length > 0 && (
                        <button onClick={exportCSV} data-testid="leads-export-csv" className="inline-flex items-center gap-2 border border-brand-border hover:border-brand-ink px-4 py-2.5 text-sm font-semibold rounded-sm">
                            <Download className="w-4 h-4" /> Exportar CSV
                        </button>
                    )}
                </div>
            </div>

            {/* Template preview / hint */}
            <div className="bg-brand-bg/40 border border-brand-border rounded-sm p-4 flex items-start gap-3" data-testid="leads-template-preview">
                <MessageCircle className="w-5 h-5 text-brand-success flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-brand-inkSoft mb-1">Mensagem padrão (clique em "WhatsApp" de um lead p/ usar)</div>
                    {template ? (
                        <div className="text-sm text-brand-ink whitespace-pre-wrap leading-relaxed line-clamp-3">{template}</div>
                    ) : (
                        <div className="text-sm text-brand-error">Nenhuma mensagem padrão definida. <a href="/admin/visual" className="underline font-semibold">Configurar agora →</a></div>
                    )}
                </div>
            </div>

            <div className="bg-white border border-brand-border rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-brand-bg border-b border-brand-border">
                            <tr className="text-left text-xs font-bold uppercase tracking-wider text-brand-inkSoft">
                                <th className="px-4 py-3">Nome</th>
                                <th className="px-4 py-3">WhatsApp</th>
                                <th className="px-4 py-3 hidden md:table-cell">Email</th>
                                <th className="px-4 py-3 hidden lg:table-cell">Cadastro</th>
                                <th className="px-4 py-3 text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((l) => {
                                const msg = fillTemplate(template, l);
                                const waUrl = buildWaLink(l.whatsapp, msg);
                                return (
                                    <tr key={l.id} className="border-b border-brand-border last:border-0 hover:bg-brand-bg/40" data-testid={`lead-row-${l.id}`}>
                                        <td className="px-4 py-3 font-semibold text-brand-ink">{l.name}</td>
                                        <td className="px-4 py-3">
                                            <span className="inline-flex items-center gap-1 text-brand-inkSoft text-xs">{l.whatsapp}</span>
                                        </td>
                                        <td className="px-4 py-3 hidden md:table-cell text-brand-inkSoft">
                                            {l.email ? (
                                                <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-brand-ink"><Mail className="w-3.5 h-3.5" /> {l.email}</a>
                                            ) : "—"}
                                        </td>
                                        <td className="px-4 py-3 hidden lg:table-cell text-xs text-brand-inkSoft">{new Date(l.created_at).toLocaleString("pt-BR")}</td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="inline-flex gap-1">
                                                <a
                                                    href={waUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`lead-wa-${l.id}`}
                                                    title="Abrir WhatsApp com mensagem pré-preenchida"
                                                    className="inline-flex items-center gap-1 bg-brand-success text-white hover:bg-brand-success/90 px-3 py-1.5 text-xs font-bold rounded-sm"
                                                >
                                                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                                                </a>
                                                <button onClick={() => remove(l.id)} data-testid={`lead-delete-${l.id}`} className="w-8 h-8 inline-flex items-center justify-center hover:bg-red-50 text-brand-error rounded-sm"><Trash2 className="w-4 h-4" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {items.length === 0 && (
                                <tr><td colSpan={5} className="px-4 py-12 text-center text-brand-inkSoft text-sm">Ainda não há leads. Quando alguém preencher o formulário "Receba novidades", aparecerá aqui.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {broadcasting && (
                <BroadcastModal items={items} template={template} onClose={() => setBroadcasting(false)} />
            )}
        </div>
    );
};

export default Leads;
